import React, { useEffect, useMemo, useState } from "react";
import { Chip, Box, Typography, CircularProgress } from "@mui/material";
import { getBookingAppointmentLeads } from "../../../../api/provider-section/provider-service-management";
import LeadDetails from "./lead-details";
import "./leads.scss";
import ListingTable from "../../../../common/components/listing-table/list-table";
import { Header } from "../../../../common/components/listing-table/listing-table-props";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../../constants/routes";
import { useNavigate } from "react-router-dom";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { useDispatch, useSelector } from "react-redux";
import AdminPaginationComponent from "../../../../common/components/admin-pagination-component/admin-pagination-component";
import { AlertComponent } from "../../../../common/components";

interface Lead {
  id: number;
  leadName: string;
  email: string;
  phoneNumber: string;
  needType: string;
  leadSource: string;
  status: string;
}

const getInterestedProductLabel = (needType: unknown) => {
  if (needType === null || needType === undefined) return "-";

  const mapSingle = (value: string) => {
    const normalized = value
      .toLowerCase()
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (["at need", "atneed", "immediate need"].includes(normalized)) {
      return "Immediate Need";
    }
    if (["pre need", "preneed", "preneed planning"].includes(normalized)) {
      return "Preneed";
    }
    return value.trim();
  };

  const items = String(needType)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length === 0) return "-";
  return items.map(mapSingle).join(", ");
};

const Leads: React.FC = () => {
  const breadcrumbLinkForList = [
    {
      label: "Leads",
      link: routes.PROVIDER_LEADS,
      disablePointerEvents: true,
    },
    {
      label: "Leads List",
      link: "#",
    },
  ];
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const pageSize = 10;
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const providerLeadRefresh = useSelector(
    (state: any) => state?.lead?.providerLeadRefresh,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1);
  }, []);

  const fetchLeads = async (page: number) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset,
      };
      const response = await getBookingAppointmentLeads(params);
      if (response.data?.success) {
        const leadsData = response.data?.data?.data?.leads || [];
        const mappedLeads: Lead[] = leadsData.map((item: any) => ({
          id: item.id,
          leadName: item.name?.trim() || "-",
          email: item.email || "-",
          phoneNumber: item.phone || "-",
          needType: getInterestedProductLabel(item.needType),
          leadSource: item.leadSource || "-",
          status: item.leadStatus || "NEW",
        }));
        setLeads(mappedLeads);
        setTotalRecords(response.data?.data?.data?.totalRecords || 0);
        dispatch({
          type: "LEAD:SET_PROVIDER_LEAD_INDICATOR",
          payload: { value: false },
        });
      } else {
        setError("Failed to fetch leads");
      }
    } catch (err) {
      console.error("Error fetching leads:", err);
      setError("An error occurred while fetching leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerLeadRefresh) {
      fetchLeads(currentPage);
      dispatch({
        type: "LEAD:REFRESH_PROVIDER_LEAD_PAGE",
        payload: { value: false },
      });
    }
  }, [providerLeadRefresh]);

  const getStatusChip = (status: string) => {
    let bgColor = "";
    let color = "";
    let displayStatus = status;
    switch (status?.toUpperCase()) {
      case "IN PROGRESS":
        bgColor = "#FFF9E6";
        color = "#D97706";
        displayStatus = "In Progress";
        break;
      case "NEW LEAD":
        bgColor = "#F5F3FF";
        color = "#8B5CF6";
        displayStatus = "New Lead";
        break;
      case "CLOSED-LOST":
        bgColor = "#FEF2F2";
        color = "#EF4444";
        displayStatus = "Closed Lost";
        break;
      case "CLOSED-WON":
        bgColor = "#ECFDF5";
        color = "#10B981";
        displayStatus = "Closed Won";
        break;
      case "CLOSED":
        bgColor = "#FEE2E2";
        color = "#EF4444";
        displayStatus = "Closed";
        break;
      default:
        bgColor = "#F3F4F6";
        color = "#374151";
        displayStatus = status || "Unknown";
    }

    return (
      <Chip
        label={displayStatus}
        size="small"
        sx={{
          backgroundColor: bgColor,
          color: color,
          fontWeight: 500,
          borderRadius: "999px",
          height: "24px",
          fontSize: "14px",
          "& .MuiChip-label": {
            paddingLeft: "8px",
            paddingRight: "8px",
          },
        }}
      />
    );
  };
  const headers: Header[] = [
    { key: "leadName", label: "Lead Name", align: "left" },
    { key: "email", label: "Email", align: "left" },
    { key: "phoneNumber", label: "Phone Number", align: "center" },
    { key: "needType", label: "Interested Product", align: "center" },
    { key: "leadSource", label: "Lead Source", align: "center" },
    { key: "status", label: "Status", align: "center" },
  ];
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
    fetchLeads(value);
  };
  const getPhoneValue = (phone: unknown) => {
    if (phone === null || phone === undefined) return "-";
    const normalized = String(phone).trim();
    return normalized ? normalized : "-";
  };
  const formattedData = leads.map((lead) => ({
    ...lead,
    phone: getPhoneValue((lead as any).phone),
    needType: getInterestedProductLabel(lead.needType),
    status: getStatusChip(lead.status),
  }));
  const handleRowClick = (row: any) => {
    setSelectedLeadId(row.id);
    const serializableBreadcrumbLink = [
      {
        label: "Leads",
        link: routes.PROVIDER_LEADS,
      },
      {
        label: "Leads List",
        link: "#",
      },
      {
        label: row.leadName,
        link: routes.PROVIDER_LEAD_DETAILS.replace(":id", String(row.id)),
        disablePointerEvents: true,
      },
    ];

    navigate(routes.PROVIDER_LEAD_DETAILS.replace(":id", String(row.id)), {
      state: {
        breadcrumbLink: serializableBreadcrumbLink,
      },
    });
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  // if (loading) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" p={4}>
  //       <CircularProgress size={40} />
  //     </Box>
  //   );
  // }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="provider-leads-main-wrapper">
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <ProfileHeader breadcrumbLink={breadcrumbLink} mode={"provider"} />
          <div
            className="provider-leads-filter-and-list-wrapper"
            style={{
              flexDirection: "column",
              height: "100%",
              //padding bottom required when we have no data
              paddingBottom: totalRecords === 0 ? "10px" : "0px",
            }}
          >
            <div className="provider-leads-listing-table-wrapper">
              <ListingTable
                headers={headers}
                data={formattedData}
                noResultsText={"No leads found for this provider"}
                onRowClick={(row: any) => {
                  handleRowClick(row);
                }}
              />
            </div>
            {totalRecords > 0 && (
              <AdminPaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
        <AlertComponent
          open={alertOpen}
          type={alertType}
          message={alertMessage || ""}
          onClose={() => setAlertOpen(false)}
        />
      </div>
    </>
  );
};

export default Leads;
